import React, { useState } from 'react'
import { observer } from 'mobx-react-lite'
import ProductScoutStore from '../ProductScout.store'
import { US_STATES, AGE_BANDS, GENDERS, CONDITIONS, HOUSEHOLD } from './wizardOptions'
import { detectPii } from './piiCheck'
import './WizardPane.component.scss'

/**
 * `WizardPane` — the ProductScout intake form: policy-qualifying questions
 * only (state, age, gender, tobacco, health conditions, who's covered — no
 * product types; recommending those is ProductScout's job). Demographic
 * only (no names or identifiers); submits to
 * `ProductScoutStore.startConversation`, after which the parent
 * `ProductScout` component swaps to the chat surface.
 *
 * Form state lives in `ProductScoutStore.intake` (single source of truth so
 * a user navigating away and back keeps their in-progress draft). The
 * `setIntakeField` action updates one field at a time. A small local
 * `touched` set tracks which fields the user has interacted with so we can
 * distinguish "unanswered" from "default boolean false" on validation —
 * matters for the tobacco field where `false` is a valid answer that should
 * still require explicit selection.
 *
 * Validation runs on submit only (no nagging during entry). Required fields:
 * state, age_band, gender, tobacco. Notes is screened for PII via the
 * patterns in `piiCheck.js`; a match blocks submit with an inline message.
 *
 * Quick question: agents can skip the profile and ask directly. Whatever
 * profile fields are already filled in go along; nothing is required.
 */
function WizardPane() {
  const intake = ProductScoutStore.intake
  // Observed — surface budget block + service errors that occur DURING
  // startConversation. Before the G7 refactor these errors arrived as
  // thrown exceptions caught by `onSubmit`; now they're set on the store
  // and the wizard observes them directly so the user gets feedback
  // instead of a stuck "Starting…" button (regression caught in QA).
  const storeError = ProductScoutStore.error
  const budgetBlocked = ProductScoutStore.budget_blocked
  const [submitting, setSubmitting] = useState(false)
  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  const [question, setQuestion] = useState('')
  const [questionError, setQuestionError] = useState(null)

  const setField = (name, value) => {
    ProductScoutStore.setIntakeField(name, value)
    setTouched(t => (t[name] ? t : { ...t, [name]: true }))
    if (errors[name]) {
      setErrors(e => {
        const next = { ...e }
        delete next[name]
        return next
      })
    }
  }

  /**
   * Toggle a value in an array-valued field (conditions, coverage_interest).
   */
  const toggleListValue = (name, value) => {
    const current = Array.isArray(intake[name]) ? intake[name] : []
    const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value]
    setField(name, next)
  }

  /**
   * Run all validation rules and return an `{ [field]: message }` map.
   * An empty map means the form is valid.
   */
  const validate = () => {
    const errs = {}
    if (!intake.state) errs.state = 'Required'
    if (!intake.age_band) errs.age_band = 'Required'
    if (!intake.gender) errs.gender = 'Required'
    if (!touched.tobacco) errs.tobacco = 'Required'
    if (intake.notes) {
      const pii = detectPii(intake.notes)
      if (pii) {
        errs.notes = `Looks like ${pii} — please rephrase without identifiers.`
      }
    }
    return errs
  }

  /**
   * Profile fields the agent has actually filled in (tobacco only once
   * answered — its `false` default isn't an answer). Sent with a quick
   * question so partial context still helps.
   */
  const filledIntake = () => {
    const out = {}
    if (intake.state) out.state = intake.state
    if (intake.age_band) out.age_band = intake.age_band
    if (intake.gender) out.gender = intake.gender
    if (touched.tobacco) out.tobacco = intake.tobacco
    if (intake.conditions?.length) out.conditions = intake.conditions
    if (intake.household) out.household = intake.household
    if (intake.notes && !detectPii(intake.notes)) out.notes = intake.notes
    return out
  }

  const onAsk = async () => {
    const q = question.trim()
    if (!q || submitting) return
    const pii = detectPii(q)
    if (pii) {
      setQuestionError(`Looks like ${pii} — please rephrase without identifiers.`)
      return
    }
    setQuestionError(null)
    ProductScoutStore.clearError()
    ProductScoutStore.clearBudgetBlock()
    setSubmitting(true)
    const conv = await ProductScoutStore.startWithQuestion(q, filledIntake())
    // On success the parent unmounts this pane (see onSubmit).
    if (!conv) setSubmitting(false)
  }

  const onSubmit = async e => {
    e.preventDefault()
    const errs = validate()
    setErrors(errs)
    if (Object.keys(errs).length > 0) return

    // Clear stale store-side errors before re-attempting so the previous
    // banner doesn't linger across submits.
    ProductScoutStore.clearError()
    ProductScoutStore.clearBudgetBlock()
    setSubmitting(true)
    const conv = await ProductScoutStore.startConversation()
    // `startConversation` (post-G7 refactor) returns the conversation on
    // success or null on error. On success the parent unmounts WizardPane
    // when `is_active` flips — we deliberately do not setState here to
    // avoid React 16's update-on-unmounted-component warning. On error,
    // we stay mounted and re-enable submit; the visible feedback comes
    // from the observed `budgetBlocked` / `storeError` block above the
    // form (rendered below).
    if (!conv) {
      setSubmitting(false)
    }
  }

  return (
    <form className='WizardPane' onSubmit={onSubmit} noValidate>
      <div className='WizardPane__quick'>
        <label className='WizardPane__heading' htmlFor='ps-wiz-question'>
          Have a quick question?
        </label>
        <div className='WizardPane__quick-row'>
          <textarea
            id='ps-wiz-question'
            className={`WizardPane__textarea${questionError ? ' has-error' : ''}`}
            rows={2}
            value={question}
            placeholder='e.g. Is Bright Life a good fit for a family of 5?'
            onChange={e => {
              setQuestion(e.target.value)
              if (questionError) setQuestionError(null)
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                onAsk()
              }
            }}
          />
          <button
            type='button'
            className='WizardPane__quick-ask'
            onClick={onAsk}
            disabled={submitting || !!budgetBlocked || question.trim().length === 0}
          >
            Ask
          </button>
        </div>
        {questionError && <span className='WizardPane__error'>{questionError}</span>}
      </div>

      <div className='WizardPane__divider' role='separator'>
        <span>or describe your prospect</span>
      </div>

      <div className='WizardPane__intro'>
        <h4 className='WizardPane__heading'>Tell me about your prospect</h4>
        <p className='WizardPane__hint'>Demographic only — no names or identifiers.</p>
      </div>

      <div className='WizardPane__field'>
        <label className='WizardPane__label' htmlFor='ps-wiz-state'>
          State <span className='WizardPane__required'>*</span>
        </label>
        <select
          id='ps-wiz-state'
          className={`WizardPane__select${errors.state ? ' has-error' : ''}`}
          value={intake.state}
          onChange={e => setField('state', e.target.value)}
        >
          <option value=''>Select…</option>
          {US_STATES.map(s => (
            <option key={s.code} value={s.code}>
              {s.name}
            </option>
          ))}
        </select>
        {errors.state && <span className='WizardPane__error'>{errors.state}</span>}
      </div>

      <div className='WizardPane__field'>
        <label className='WizardPane__label'>
          Age band <span className='WizardPane__required'>*</span>
        </label>
        <div className='WizardPane__chips' role='radiogroup' aria-label='Age band'>
          {AGE_BANDS.map(ab => {
            const isActive = intake.age_band === ab
            return (
              <button
                key={ab}
                type='button'
                role='radio'
                aria-checked={isActive}
                className={`WizardPane__chip${isActive ? ' is-active' : ''}`}
                onClick={() => setField('age_band', ab)}
              >
                {ab}
              </button>
            )
          })}
        </div>
        {errors.age_band && <span className='WizardPane__error'>{errors.age_band}</span>}
      </div>

      <div className='WizardPane__field'>
        <label className='WizardPane__label'>
          Gender <span className='WizardPane__required'>*</span>
        </label>
        <div className='WizardPane__chips' role='radiogroup' aria-label='Gender'>
          {GENDERS.map(g => {
            const isActive = intake.gender === g.value
            return (
              <button
                key={g.value}
                type='button'
                role='radio'
                aria-checked={isActive}
                className={`WizardPane__chip${isActive ? ' is-active' : ''}`}
                onClick={() => setField('gender', g.value)}
              >
                {g.label}
              </button>
            )
          })}
        </div>
        {errors.gender && <span className='WizardPane__error'>{errors.gender}</span>}
      </div>

      <div className='WizardPane__field'>
        <label className='WizardPane__label'>
          Tobacco user <span className='WizardPane__required'>*</span>
        </label>
        <div className='WizardPane__chips' role='radiogroup' aria-label='Tobacco user'>
          <button
            type='button'
            role='radio'
            aria-checked={touched.tobacco && intake.tobacco === true}
            className={`WizardPane__chip${touched.tobacco && intake.tobacco === true ? ' is-active' : ''}`}
            onClick={() => setField('tobacco', true)}
          >
            Yes
          </button>
          <button
            type='button'
            role='radio'
            aria-checked={touched.tobacco && intake.tobacco === false}
            className={`WizardPane__chip${touched.tobacco && intake.tobacco === false ? ' is-active' : ''}`}
            onClick={() => setField('tobacco', false)}
          >
            No
          </button>
        </div>
        {errors.tobacco && <span className='WizardPane__error'>{errors.tobacco}</span>}
      </div>

      <div className='WizardPane__field'>
        <label className='WizardPane__label'>Health conditions</label>
        <p className='WizardPane__hint'>Select all that apply. None selected = healthy.</p>
        <div className='WizardPane__chips' role='group' aria-label='Health conditions'>
          <button
            type='button'
            aria-pressed={!(intake.conditions || []).length}
            className={`WizardPane__chip${!(intake.conditions || []).length ? ' is-active' : ''}`}
            onClick={() => setField('conditions', [])}
          >
            None — healthy
          </button>
          {CONDITIONS.map(c => {
            const isActive = (intake.conditions || []).includes(c.value)
            return (
              <button
                key={c.value}
                type='button'
                aria-pressed={isActive}
                className={`WizardPane__chip${isActive ? ' is-active' : ''}`}
                onClick={() => toggleListValue('conditions', c.value)}
              >
                {c.label}
              </button>
            )
          })}
        </div>
      </div>

      <div className='WizardPane__field'>
        <label className='WizardPane__label'>Coverage for</label>
        <div className='WizardPane__chips' role='radiogroup' aria-label='Coverage for'>
          {HOUSEHOLD.map(h => {
            const isActive = intake.household === h.value
            return (
              <button
                key={h.value}
                type='button'
                role='radio'
                aria-checked={isActive}
                className={`WizardPane__chip${isActive ? ' is-active' : ''}`}
                onClick={() => setField('household', isActive ? '' : h.value)}
              >
                {h.label}
              </button>
            )
          })}
        </div>
      </div>

      <div className='WizardPane__field'>
        <label className='WizardPane__label' htmlFor='ps-wiz-notes'>
          Notes
        </label>
        <textarea
          id='ps-wiz-notes'
          className={`WizardPane__textarea${errors.notes ? ' has-error' : ''}`}
          placeholder='Demographic context only — no names, no identifiers.'
          value={intake.notes}
          onChange={e => setField('notes', e.target.value)}
          rows={3}
        />
        {errors.notes && <span className='WizardPane__error'>{errors.notes}</span>}
      </div>

      {budgetBlocked && (
        <div className='WizardPane__budget-block' role='alert'>
          <strong>{`ProductScout ${budgetBlocked.scope === 'monthly' ? 'monthly' : 'daily'} limit reached.`}</strong>
          <span className='WizardPane__budget-block-detail'>
            {budgetBlocked.message}
            {budgetBlocked.reset_at ? ` Resets ${formatWizardResetTime(budgetBlocked.reset_at)}.` : ''}
          </span>
          <button
            type='button'
            className='WizardPane__budget-block-dismiss'
            onClick={() => ProductScoutStore.clearBudgetBlock()}
            aria-label='Dismiss budget-block banner'
          >
            Dismiss
          </button>
        </div>
      )}

      {!budgetBlocked && storeError && (
        <div className='WizardPane__submit-error' role='alert'>
          {storeError}
          <button
            type='button'
            className='WizardPane__submit-error-dismiss'
            onClick={() => ProductScoutStore.clearError()}
            aria-label='Dismiss error'
          >
            ×
          </button>
        </div>
      )}

      <button type='submit' className='WizardPane__submit' disabled={submitting || !!budgetBlocked}>
        {submitting ? 'Starting…' : 'Start Conversation'}
      </button>
    </form>
  )
}

/**
 * Format the server-provided reset_at ISO timestamp for the wizard's
 * budget-block banner. Mirrors the formatter in ChatPane so the two
 * surfaces stay visually consistent. Falls back to the raw string when
 * parsing fails.
 */
function formatWizardResetTime(iso) {
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    return d.toLocaleString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      month: 'short',
      day: 'numeric',
    })
  } catch (e) {
    return iso
  }
}

export default observer(WizardPane)
